import {
  Component,
  ChangeDetectionStrategy,
  ComponentRef,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  Observable,
  switchMap,
  of,
  finalize,
  shareReplay,
  map,
  EMPTY,
  merge,
  tap,
} from 'rxjs';
import { v4 as uuid } from 'uuid';
import { untilDestroyed } from '@ngneat/until-destroy';
import { HttpErrorResponse } from '@angular/common/http';

import { DprDetailsPageBaseComponent } from '@dpr-details/models/component';
import {
  DprAccumulationService,
  DprDetailsService,
  DprFormulaCalculationService,
} from '@dpr-details/services';
import { GridSync } from '@grid/models';
import {
  DprDetailsSync,
  DprTableOfColumns,
  DprTableOfColumnDetail,
} from '@shared/models/dpr';
import { TableOfColumnWithData } from '@shared/models/project-setup';

import { DprTableOfColumnsRegistrationsGridComponent } from '@dpr-details/components';
import { DprTableOfColumnsGridService } from '@dpr-details/services/grid';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { DprTableOfColumnGridSync } from '@dpr-details/models/sync/dpr-table-of-column-grid-sync.model';
import {
  DprTableOfColumnsAccumulation,
  DprTableOfColumnsFormulaContext,
} from '@dpr-details/models';
import {
  applyCarryOverValues,
  getDefaultSubMenuItems,
  normalizeAnchorValue,
  withoutDerivedTableOfColumnItems,
  withoutInactiveAnchorRows,
} from '@dpr-details/utils';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';
import { StaticMessage } from '@shared/enums';
import { NavigationMenuItem } from '@shared/models';
import { ScreenSizeService } from '@shared/services';

@Component({
  selector: 'app-dpr-table-of-columns-page',
  templateUrl: './dpr-table-of-columns-page.component.html',
  styleUrls: ['./dpr-table-of-columns-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprTableOfColumnsPageComponent
  extends DprDetailsPageBaseComponent<DprTableOfColumnDetail>
  implements OnInit
{
  labeling = {};

  StaticMessage = StaticMessage;

  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = getDefaultSubMenuItems();

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected dprAttachmentService: DprAttachmentService,
    private dprTableOfColumnsGridService: DprTableOfColumnsGridService,
    private dprDetailsService: DprDetailsService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprAccumulationService: DprAccumulationService,
    private dprFormulaCalculationService: DprFormulaCalculationService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute, dprAttachmentService);
  }

  //#region Getters

  private get dprTableOfColumn(): DprTableOfColumns {
    return this.dprDetails.dprTableOfColumns.find(
      (dprTableOfColumn) =>
        dprTableOfColumn.tableOfColumnId === this.preselectedTableOfColumnId
    );
  }

  private get preselectedTableOfColumnId(): number {
    return +this.activatedRoute?.snapshot.params.tableOfColumnId;
  }

  private get tableOfColumn(): TableOfColumnWithData | undefined {
    return this.selectedSubproject?.data?.tableOfColumns?.find(
      (tableOfColumn) => tableOfColumn.id === this.preselectedTableOfColumnId
    );
  }

  private get accumulation(): DprTableOfColumnsAccumulation | undefined {
    return this.dprAccumulationService.getTableOfColumnsAccumulation(
      this.preselectedTableOfColumnId
    );
  }

  private get formulaContext(): DprTableOfColumnsFormulaContext | undefined {
    return this.dprAccumulationService.getTableOfColumnsFormulaContext(
      this.preselectedTableOfColumnId
    );
  }

  //#endregion

  ngOnInit(): void {
    super.ngOnInit();

    this.preselectedItem =
      this.activatedRoute?.firstChild?.snapshot?.url[0]?.path;

    this.recalculateDerivedCellsOnEdit();
  }

  save(
    _?,
    isAutoSync?: boolean,
    isAutoSyncFromAdminChange?: boolean
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    // store activated component changes
    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(
            this.registrationsChanges,
            this.attachmentsChanges
          );

    const saveChanges$ = validateChanges$.pipe(
      untilDestroyed(this),
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(
          this.registrationsChanges,
          this.attachmentsChanges,
          this.comment,
          isAutoSyncFromAdminChange ?? false
        );
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  //#region Protected methods

  protected setupData(): void {
    this.registrationsGridData = this.buildRegistrationsGridData(
      withoutInactiveAnchorRows(
        this.dprTableOfColumn?.dprTableOfColumnsDetails,
        this.tableOfColumn
      )
    );
    this.attachmentsGridData =
      this.dprAttachmentService.mapToGridData(
        this.dprTableOfColumn?.dprTableOfColumnsAttachment
      ) ?? [];
    this.comment = this.dprTableOfColumn?.comment;
  }

  protected isRegistrationComponent(component: ComponentRef<any>): boolean {
    return component instanceof DprTableOfColumnsRegistrationsGridComponent;
  }

  protected getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprTableOfColumnDetail> {
    const offlineChanges = dprDetailsOfflineChanges?.dprTableOfColumns?.find(
      (x) => x.tableOfColumnId === this.preselectedTableOfColumnId
    )?.syncDprTableOfColumnDetails;

    if (!offlineChanges) {
      return null;
    }

    // Rows hidden for an inactive anchor value keep their pending changes here: an
    // offline save replaces the table's pending changes with what the grid returns,
    // so leaving theirs out would lose those edits on the next save.
    const offlineGridData = this.dprTableOfColumnsGridService.buildGridData(
      [
        ...(offlineChanges.addedList ?? []),
        ...(offlineChanges.updatedList ?? []),
      ],
      this.accumulation,
      this.formulaContext
    );

    return this.getOfflineGridDataChanges(offlineGridData, offlineChanges);
  }

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprAttachmentsGridData> {
    const offlineChanges = dprDetailsOfflineChanges?.dprTableOfColumns?.find(
      (x) => x.tableOfColumnId === this.preselectedTableOfColumnId
    )?.syncDprTableOfColumnAttachments;
    return this.getOfflineAttachmentGridDataChanges(offlineChanges);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  //#endregion

  //#region Private methods

  private validateChanges(
    registrationsChanges: GridSync<DprTableOfColumnDetail>,
    attachmentsChanges: GridSync<DprAttachmentsGridData>
  ): Observable<boolean> {
    if (
      !this.dprTableOfColumnsGridService.validateChanges(
        registrationsChanges,
        this.tableOfColumn
      )
    ) {
      return of(false);
    }

    // Rows hidden for an inactive anchor value are kept, because the offline
    // changes still carry their pending edits.
    if (
      !this.isAppOnline &&
      !this.dprTableOfColumnsGridService.validateFormulaChanges(
        registrationsChanges,
        this.buildRegistrationsGridData(
          this.dprTableOfColumn?.dprTableOfColumnsDetails
        ),
        this.formulaContext,
        this.accumulation,
        this.tableOfColumn
      )
    ) {
      return of(false);
    }

    if (!this.dprAttachmentService.validateChanges(attachmentsChanges)) {
      return of(false);
    }

    return of(true);
  }

  private buildRegistrationsGridData(
    details: DprTableOfColumnDetail[]
  ): DprTableOfColumnDetail[] {
    return this.dprTableOfColumnsGridService.buildGridData(
      withoutDerivedTableOfColumnItems(
        details,
        this.tableOfColumn,
        this.dprAccumulationService.getTableOfColumnsStoredItemIds(
          this.preselectedTableOfColumnId
        )
      ),
      this.accumulation,
      this.formulaContext
    );
  }

  private saveChanges(
    registrationsChanges: GridSync<DprTableOfColumnDetail>,
    attachmentsChanges: GridSync<DprAttachmentsGridData>,
    comment: string,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprTableOfColumns> {
    const dprTableOfColumnId = this.dprTableOfColumn?.id ?? uuid();
    const syncDprTableOfColumnsDetails =
      this.dprTableOfColumnsGridService.mapToGridSync(
        dprTableOfColumnId,
        registrationsChanges,
        this.dprTableOfColumn?.dprTableOfColumnsDetails ?? [],
        this.selectedSubproject.data.tableOfColumns.find(
          (y) => y.id === this.preselectedTableOfColumnId
        )?.tableOfColumnItems ?? []
      );

    const syncData: DprTableOfColumnGridSync =
      this.dprDetailsSyncModelBuilderService.forDprTableOfColumns(
        this.selectedSubproject.id,
        this.dprDetails.id,
        dprTableOfColumnId,
        this.preselectedTableOfColumnId,
        syncDprTableOfColumnsDetails,
        attachmentsChanges,
        comment
      );
    const saveChanges$ = this.dprDetailsService
      .syncDprTableOfColumn(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.selectedSubproject?.data?.tableOfColumns?.find(
            (x) => x.id === this.preselectedTableOfColumnId
          )?.name ?? '',
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprTableOfColumns): void {
    // A save's answer carries no previous values, so the table keeps the ones it
    // was loaded with, which its carry-overs and running totals are worked out from.
    this.offlineFacade.setDprTableOfColumns(
      {
        ...result,
        previousValues:
          result.previousValues ?? this.dprTableOfColumn?.previousValues,
      },
      this.dprDetails.id
    );
    this.refreshData();
  }

  /**
   * The registrations grid sits behind the router outlet, so its edits are picked
   * up through the activated component. They have to be handled synchronously:
   * the grid repaints the edited row as soon as they return.
   */
  private recalculateDerivedCellsOnEdit(): void {
    this.activatedComponent$
      .pipe(
        switchMap((component) =>
          component instanceof DprTableOfColumnsRegistrationsGridComponent
            ? merge(
                component.anchorEdited.pipe(
                  tap((data) => this.anchorValueChanged(component, data))
                ),
                component.formulaInputEdited.pipe(
                  tap((data) => this.recalculateFormulas(component, data))
                )
              )
            : EMPTY
        ),
        untilDestroyed(this)
      )
      .subscribe();
  }

  /**
   * Refills the row's accumulated cells for the anchor value it now carries, then
   * recalculates its formulas, whose self-references resolve through the anchor.
   * The values go through `editCell` rather than straight onto the row so that the
   * summary footer of each column is recomputed too.
   */
  private anchorValueChanged(
    grid: DprTableOfColumnsRegistrationsGridComponent,
    data: DprTableOfColumnDetail
  ): void {
    const accumulation = this.accumulation;

    if (accumulation) {
      applyCarryOverValues(
        accumulation,
        normalizeAnchorValue(data[accumulation.anchorColumnId]),
        (itemId, value) => {
          if ((data[itemId] ?? null) !== value) {
            grid.api?.editCell(data, `${itemId}`, value);
          }
        }
      );
    }

    this.recalculateFormulas(grid, data);
  }

  private recalculateFormulas(
    grid: DprTableOfColumnsRegistrationsGridComponent,
    data: DprTableOfColumnDetail
  ): void {
    const formulaContext = this.formulaContext;

    if (!formulaContext) {
      return;
    }

    this.dprFormulaCalculationService
      .calculateTableOfColumnsRows(formulaContext, [data])
      .get(String(data.id))
      ?.forEach((value, itemId) => {
        if ((data[itemId] ?? null) !== value) {
          grid.api?.editCell(data, `${itemId}`, value);
        }
      });
  }
}
